import * as React from 'react';

import CareerCertificateItem from 'bundles/unified-description-page-common/components/modules/syllabus/CareerCertificateItem';
import CourseItemContent from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemContent';
import CourseItemSubtitle from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemSubtitle';
import CourseItemThumbnail from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemThumbnail';
import EligibleDegreesItem from 'bundles/unified-description-page-common/components/modules/syllabus/EligibleDegreesItem';
import SyllabusItem from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItem';
import SyllabusItemTitle from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import SyllabusModule from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusModule';
import InstructorAndPartnerInfo from 'bundles/unified-description-page-common/components/shared/InstructorAndPartnerInfo';
import TogglableDescription from 'bundles/unified-description-page-common/components/shared/TogglableDescription';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import getNumberOfInstructorsToShow from 'bundles/unified-description-page-common/utils/getNumberOfInstructorsToShow';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getSpecializationDescription from 'bundles/unified-description-page-common/utils/getSpecializationDescription';
import { getSpecializationSyllabusTitle } from 'bundles/unified-description-page-common/utils/syllabus';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: EnterpriseSpecializationPageFieldsFragment | ConsumerSpecializationPageFieldsFragment;
  config?: {
    disableCta?: boolean;
    showPathwaysXDP?: boolean;
  };
};

const SyllabusModuleRenderer = ({ pageData, config }: Props) => {
  const { getCourseLink } = usePageInfo();

  if (!pageData.courses || !pageData.courses.length) {
    return null;
  }

  const descriptionWithProjectOverview = getSpecializationDescription(
    pageData.cmlDescription,
    pageData.projectsOverview
  );

  return (
    <SyllabusModule
      anchorId={SPECIALIZATION_NAV_ITEMS.COURSES}
      title={getSpecializationSyllabusTitle(pageData.productVariant, pageData.courses.length, pageData.slug)}
      description={
        pageData.cmlLearningObjectives && (
          <TogglableDescription
            description={pageData.description}
            cmlDescription={descriptionWithProjectOverview}
            trackingName="syllabus_description"
          />
        )
      }
      slug={pageData.slug}
      syllabusItems={pageData.courses.map((course, index) => {
        return (
          <SyllabusItem
            // eslint-disable-next-line
            toggleButtonLabel={_t('Course details')}
            toggleButtonAriaLabel={_t('Course details for #{courseName}', { courseName: course.name })}
            key={course.id}
            itemIndex={index + 1}
            multiActionHeader
            trackingName="syllabus_course"
            thumbnail={
              config?.showPathwaysXDP && (
                // @ts-expect-error URL: string
                <CourseItemThumbnail thumbnailUrl={course.photoUrl} thumbnailAltText={course.name} />
              )
            }
          >
            <SyllabusItemTitle
              title={course.name}
              slug={pageData.slug}
              link={getCourseLink({ courseSlug: course.slug, specializationSlug: pageData.slug })}
              // eslint-disable-next-line
              linkA11yLabel={`${_t('Course #{courseIndex}:', { courseIndex: index + 1 })} ${course.name}`}
            />
            <CourseItemSubtitle
              courseIndex={index + 1}
              // @ts-expect-error Duration: string
              totalContentDuration={course.totalContentDuration}
              rating={config?.showPathwaysXDP ? undefined : course.ratings?.averageFiveStarRating}
              ratingCount={course.ratings?.ratingCount}
            />
            <CourseItemContent
              s12nSlug={pageData.slug}
              courseSlug={course.slug}
              skills={course.skills}
              learningObjectives={course.cmlLearningObjectives}
              description={course.description}
              fixedSession={'fixedSession' in course ? course?.fixedSession : undefined}
            />
          </SyllabusItem>
        );
      })}
      sidebar={
        <InstructorAndPartnerInfo
          instructors={pageData.instructors}
          numberOfInstructorsToShow={getNumberOfInstructorsToShow(
            pageData.instructors?.length,
            pageData.courses?.length
          )}
          partners={pageData.partners}
          industryPartners={pageData.companyLogos}
          trackingLocation="syllabus"
          disableCTA={config?.disableCta}
        />
      }
      footer={
        config?.showPathwaysXDP && (
          <React.Fragment>
            <CareerCertificateItem />
            <EligibleDegreesItem
              degrees={pageData.degrees}
              partners={pageData.partners}
              slug={pageData.slug}
              productTags={pageData.productTags}
              productVariant={pageData.productVariant}
            />
          </React.Fragment>
        )
      }
      addExtraDesktopPadding={config?.showPathwaysXDP}
    />
  );
};

export default SyllabusModuleRenderer;
